import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Button, Skeleton, Typography } from '@mui/material'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { makeStyles } from 'tss-react/mui'
import { useDispatch } from 'react-redux'
import { Security } from '../../proto/generated/security'
import { GetTechnicalsResponse } from '../../proto/generated/overview-widget'
import { getTechnicals } from '../../services/apis/securities'
import { getSecurityKey } from '../../util/security'
import {
    countActions,
    mapScoreToLabel,
    mergeActionCounts,
    scoreFromActions,
    toTechnicalsRows
} from '../../util/technicals'
import { setTechnicalsPanelOpen } from '../../reducer/home.reducer'
import TechnicalsSummaryCard from './TechnicalsSummaryCard'

type TechnicalsSectionProps = {
    security: Security | undefined
}

const useStyles = makeStyles()((theme) => ({
    container: {
        padding: theme.spacing(1.5, 2, 2)
    },
    header: {
        fontSize: '15px',
        fontWeight: 600,
        marginBottom: theme.spacing(1.5)
    },
    gauge: {
        maxWidth: 220,
        margin: '0 auto'
    },
    moreButton: {
        marginTop: theme.spacing(1.5),
        textTransform: 'none',
        fontSize: '13px',
        fontWeight: 500,
        justifyContent: 'space-between'
    }
}))

const TechnicalsSection = ({ security }: TechnicalsSectionProps) => {
    const { classes } = useStyles()
    const dispatch = useDispatch()
    const [response, setResponse] = useState<GetTechnicalsResponse | null>(null)
    const [loading, setLoading] = useState(false)

    const handleOpenTechnicals = useCallback(() => {
        dispatch(setTechnicalsPanelOpen(true))
    }, [dispatch])

    const securityKey = security ? getSecurityKey(security.exchange, security.code) : null

    useEffect(() => {
        if (!security || !securityKey) {
            setResponse(null)
            return
        }

        let cancelled = false
        setLoading(true)
        setResponse(null)

        ;(async () => {
            try {
                const data = await getTechnicals({
                    exchange: security.exchange,
                    code: security.code
                })
                if (cancelled) return
                setResponse(data)
            } catch (error) {
                if (!cancelled) {
                    console.error('Error fetching technicals overview', error)
                    setResponse(null)
                }
            } finally {
                if (!cancelled) setLoading(false)
            }
        })()

        return () => {
            cancelled = true
        }
    }, [security, securityKey])

    const summary = useMemo(() => {
        const oscillatorRows = toTechnicalsRows(response?.oscillators ?? [])
        const movingAverageRows = toTechnicalsRows(response?.movingAverages ?? [])
        if (oscillatorRows.length === 0 && movingAverageRows.length === 0) return null

        const oscillatorScore = scoreFromActions(oscillatorRows)
        const movingAverageScore = scoreFromActions(movingAverageRows)
        const score = (oscillatorScore + movingAverageScore) / 2

        return {
            score,
            label: mapScoreToLabel(score),
            counts: mergeActionCounts(countActions(oscillatorRows), countActions(movingAverageRows))
        }
    }, [response])

    if (!security) return null

    if (loading) {
        return (
            <Box className={classes.container}>
                <Typography className={classes.header}>Technicals</Typography>
                <Skeleton variant="rounded" height={160} />
            </Box>
        )
    }

    if (!summary) return null

    return (
        <Box className={classes.container}>
            <Typography className={classes.header}>Technicals</Typography>
            <Box className={classes.gauge}>
                <TechnicalsSummaryCard
                    title="Summary"
                    label={summary.label}
                    score={summary.score}
                    counts={summary.counts}
                    emphasized
                    showTitle={false}
                    showCounts={false}
                />
            </Box>
            <Button
                fullWidth
                variant="outlined"
                color="inherit"
                size="small"
                className={classes.moreButton}
                endIcon={<ChevronRightIcon fontSize="small" />}
                onClick={handleOpenTechnicals}
            >
                More technicals
            </Button>
        </Box>
    )
}

export default memo(TechnicalsSection)
